import { useState, useEffect, useRef } from 'react';
import {
  Lock, Settings, Check, X, RefreshCw, Key, Plus, Copy, Pencil,
  Smartphone, Unlink, Globe, Loader, Trash2, AlertTriangle
} from 'lucide-react';
import { getPublicLink, devicesAPI, cryptoAPI } from '../api';
import './FileEditor.css';

/**
 * Панель UUID-ключей доступа одного файла подписки.
 *
 * Вынесена из FileEditor, потому что её целиком показывает и кабинет дилера:
 * дилер работает ровно с этим — выдаёт ключи и раздаёт ссылки, а содержимое
 * файла (сами VPN-ссылки) ему не показывается вообще.
 *
 * isAdmin=false убирает то, что дилеру не положено: смену лимита устройств
 * и отзыв всех ключей разом (сервер их всё равно вернёт 403).
 *
 * cryptoOnly=true — дилер работает только с Happ: обычная ссылка на подписку
 * прячется совсем, остаётся только crypto4. Ограничение чисто интерфейсное —
 * оно задаёт, что дилер копирует и раздаёт, а не режет доступ к данным.
 */
function AccessKeysPanel({ fileName, isAdmin = true, cryptoOnly = false, refreshToken = 0, onKeysChanged, onError }) {
  const [keyData, setKeyData] = useState(null);         // { filename, maxDevices, keyCount, keys }
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [unbindingAll, setUnbindingAll] = useState(false);
  const [editingKeyId, setEditingKeyId] = useState(null);  // uuid being relabeled
  const [editingKeyLabel, setEditingKeyLabel] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);         // uuid whose link was copied
  const [copiedAllKeys, setCopiedAllKeys] = useState(false); // «скопировать все ссылки» отработал
  const [crypto4Loading, setCrypto4Loading] = useState(null); // uuid being converted
  const [crypto4Results, setCrypto4Results] = useState({});   // { [uuid]: crypto4Url }
  const [crypto4Copied, setCrypto4Copied] = useState(null);   // uuid whose crypto4 was copied
  const [crypto4AllRunning, setCrypto4AllRunning] = useState(false); // идёт массовый crypto4
  const [crypto4AllDone, setCrypto4AllDone] = useState(0);           // сколько ключей обработано
  const [copiedAllCrypto4, setCopiedAllCrypto4] = useState(false);   // «копировать все crypto4» отработал

  // Limit editor (только админ)
  const [isEditingLimit, setIsEditingLimit] = useState(false);
  const [newLimit, setNewLimit] = useState(2);

  const labelInputRef = useRef(null);

  // Режим берём и из пропа, и из ответа сервера: сервер знает наверняка, а проп
  // приходит из /auth/check и может отставать, если админ переключил тип только что.
  const cryptoMode = cryptoOnly || keyData?.cryptoOnly === true;

  const setOpError = (msg) => onError?.(msg);

  useEffect(() => {
    // Смена файла — сбрасываем всё локальное состояние, иначе crypto4-ссылки
    // предыдущего файла остались бы висеть под чужими ключами.
    setCrypto4Results({});
    setEditingKeyId(null);
    loadKeyData();
  }, [fileName]);

  // Обновление по кнопке. Отдельным эффектом от смены файла: посчитанные crypto4-ссылки
  // тут терять нельзя — обновляют, чтобы увидеть, подключился ли клиент, а не чтобы всё стереть.
  useEffect(() => {
    if (refreshToken) loadKeyData();
  }, [refreshToken]);

  // Focus label input when editing starts
  useEffect(() => {
    if (editingKeyId && labelInputRef.current) {
      labelInputRef.current.focus();
      labelInputRef.current.select();
    }
  }, [editingKeyId]);

  const loadKeyData = async () => {
    setIsLoadingKeys(true);
    try {
      const res = await devicesAPI.getOne(fileName);
      setKeyData(res.data);
    } catch (err) {
      if (err.response?.status !== 401) console.error('Failed to load key data:', err);
      setKeyData(null);
    } finally {
      setIsLoadingKeys(false);
    }
  };

  const handleGenerateKey = async () => {
    setGeneratingKey(true);
    try {
      await devicesAPI.generateKey(fileName);
      await loadKeyData();
      onKeysChanged?.();
    } catch (err) {
      setOpError('Ошибка создания ключа: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleRevokeKey = async (key, label) => {
    if (!confirm(`Удалить ключ "${label}"?\nЭто немедленно заблокирует доступ для этого устройства.`)) return;
    try {
      await devicesAPI.revokeKey(fileName, key);
      await loadKeyData();
      onKeysChanged?.();
    } catch (err) {
      setOpError('Ошибка удаления ключа: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUnbindKey = async (key) => {
    try {
      await devicesAPI.unbindKey(fileName, key);
      await loadKeyData();
    } catch (err) {
      setOpError('Ошибка при сбросе привязки: ' + (err.response?.data?.error || err.message));
    }
  };

  // Массовый аналог оранжевой кнопки у устройства: снимает привязку со ВСЕХ ключей файла.
  // Ключи при этом живут дальше — клиентам не нужно раздавать новые ссылки.
  const handleUnbindAll = async () => {
    const total = keyData?.keys?.length || 0;
    if (!confirm(`Сбросить привязку у всех ключей (${total}) для "${fileName}"?\nСсылки останутся рабочими — каждый ключ просто привяжется к новому устройству при следующем подключении.`)) return;
    setUnbindingAll(true);
    try {
      await devicesAPI.unbindAllKeys(fileName);
      await loadKeyData();
    } catch (err) {
      setOpError('Ошибка сброса привязок: ' + (err.response?.data?.error || err.message));
    } finally {
      setUnbindingAll(false);
    }
  };

  const handleRevokeAll = async () => {
    if (!confirm(`Отозвать ВСЕ ключи для "${fileName}"?\nВсе устройства потеряют доступ.`)) return;
    try {
      await devicesAPI.resetAll(fileName);
      await loadKeyData();
      onKeysChanged?.();
    } catch (err) {
      setOpError('Ошибка сброса ключей: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleStartRelabel = (key) => {
    setEditingKeyId(key.key);
    setEditingKeyLabel(key.label);
  };

  const handleSaveLabel = async (keyUuid) => {
    const label = editingKeyLabel.trim();
    if (!label) { setEditingKeyId(null); return; }
    try {
      await devicesAPI.relabelKey(fileName, keyUuid, label);
      setKeyData(prev => ({
        ...prev,
        keys: prev.keys.map(k => k.key === keyUuid ? { ...k, label } : k)
      }));
    } catch (err) {
      setOpError('Ошибка переименования: ' + (err.response?.data?.error || err.message));
    } finally {
      setEditingKeyId(null);
    }
  };

  // Clipboard API есть только в secure context (https/localhost) — по http нужен textarea-фолбэк.
  const writeClipboard = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;left:-9999px';
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    document.execCommand('copy');
    ta.remove();
  };

  const handleCopyKeyLink = async (key) => {
    try {
      await writeClipboard(getPublicLink(fileName, key));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      setOpError('Не удалось скопировать: ' + err.message);
    }
  };

  // Все ссылки файла одним куском: «метка устройства» + ссылка, пустая строка между блоками —
  // чтобы можно было разом скопировать и дальше раздавать по устройствам, не кликая по каждому ключу.
  const handleCopyAllKeyLinks = async () => {
    const keys = keyData?.keys || [];
    if (!keys.length) return;
    const text = keys
      .map((k, i) => `${k.label || `Устройство ${i + 1}`}\n${getPublicLink(fileName, k.key)}`)
      .join('\n\n');
    try {
      await writeClipboard(text);
      setCopiedAllKeys(true);
      setTimeout(() => setCopiedAllKeys(false), 2000);
    } catch (err) {
      setOpError('Не удалось скопировать: ' + err.message);
    }
  };

  // У дилера «только Happ» полного UUID нет — crypto4-ссылку собирает сервер.
  const fetchCrypto4 = async (keyUuid) => {
    if (cryptoMode) {
      const res = await devicesAPI.crypto4(fileName, keyUuid);
      return res.data.crypto4Url;
    }
    const res = await cryptoAPI.toCrypto4(getPublicLink(fileName, keyUuid));
    return res.data.crypto4Url;
  };

  const handleCrypto4 = async (keyUuid) => {
    if (crypto4Loading === keyUuid) return;
    setCrypto4Loading(keyUuid);
    try {
      const url = await fetchCrypto4(keyUuid);
      setCrypto4Results(prev => ({ ...prev, [keyUuid]: url }));
    } catch (err) {
      setOpError('Ошибка crypto4: ' + (err.response?.data?.error || err.message));
    } finally {
      setCrypto4Loading(null);
    }
  };

  // Crypto4 для всех ключей разом. Идём последовательно: crypto.happ.su — внешний сервис,
  // параллельная пачка из 8+ запросов ловит троттлинг. Уже посчитанные ключи пропускаем.
  const handleCrypto4All = async () => {
    const keys = keyData?.keys || [];
    const pending = keys.filter(k => !crypto4Results[k.key]);
    if (!pending.length || crypto4AllRunning) return;
    setCrypto4AllRunning(true);
    setCrypto4AllDone(0);
    let failed = 0;
    for (const k of pending) {
      setCrypto4Loading(k.key);
      try {
        const url = await fetchCrypto4(k.key);
        setCrypto4Results(prev => ({ ...prev, [k.key]: url }));
      } catch {
        failed++;
      }
      setCrypto4AllDone(n => n + 1);
    }
    setCrypto4Loading(null);
    setCrypto4AllRunning(false);
    if (failed) setOpError(`Crypto4: не удалось получить ${failed} из ${pending.length} ссылок — нажми ещё раз`);
  };

  const handleCopyCrypto4 = async (keyUuid) => {
    const url = crypto4Results[keyUuid];
    if (!url) return;
    try {
      await writeClipboard(url);
      setCrypto4Copied(keyUuid);
      setTimeout(() => setCrypto4Copied(null), 2000);
    } catch (err) {
      setOpError('Не удалось скопировать: ' + err.message);
    }
  };

  // Все посчитанные crypto4-ссылки одним списком, в том же формате, что и обычные ссылки.
  const handleCopyAllCrypto4 = async () => {
    const keys = keyData?.keys || [];
    const ready = keys.filter(k => crypto4Results[k.key]);
    if (!ready.length) return;
    const text = ready
      .map((k, i) => `${k.label || `Устройство ${i + 1}`}\n${crypto4Results[k.key]}`)
      .join('\n\n');
    try {
      await writeClipboard(text);
      setCopiedAllCrypto4(true);
      setTimeout(() => setCopiedAllCrypto4(false), 2000);
    } catch (err) {
      setOpError('Не удалось скопировать: ' + err.message);
    }
  };

  const handleSaveLimit = async () => {
    if (newLimit < 1 || newLimit > 20) { setOpError('Лимит должен быть от 1 до 20'); return; }
    const currentKeyCount = keyData?.keyCount || 0;
    if (newLimit < currentKeyCount) {
      const excess = currentKeyCount - newLimit;
      if (!confirm(
        `Лимит будет снижен до ${newLimit}.\n` +
        `${excess} ключ(ей) будут автоматически отозваны (самые новые).\n` +
        `Эти устройства потеряют доступ. Продолжить?`
      )) return;
    }
    try {
      await devicesAPI.setLimit(fileName, newLimit);
      setIsEditingLimit(false);
      await loadKeyData();
      onKeysChanged?.();
    } catch (err) {
      setOpError('Ошибка установки лимита: ' + (err.response?.data?.error || err.message));
    }
  };

  const canGenerateKey = keyData && keyData.keyCount < keyData.maxDevices;

  // Сколько ключей уже имеют посчитанную crypto4-ссылку — от этого зависит кнопка «Все crypto4»
  const crypto4ReadyCount = (keyData?.keys || []).filter(k => crypto4Results[k.key]).length;

  return (
    <div className="footer-section">
      <div className="section-label">
        <span className="section-label-text"><Lock size={12} /> Ключи доступа</span>
        {/* Перечитать только ключи: подключился ли клиент, какой IP, какое приложение.
            Верхняя кнопка «Обновить» тянет весь список файлов — здесь это лишняя работа. */}
        <button
          onClick={loadKeyData}
          disabled={isLoadingKeys}
          className="btn btn-secondary btn-xs"
          title="Обновить ключи — увидеть, подключился ли клиент"
          style={{ marginLeft: 'auto', marginRight: 6 }}
        >
          <RefreshCw size={11} className={isLoadingKeys ? 'spin-icon' : undefined} /> Обновить
        </button>
        {keyData && (isAdmin
          ? !isEditingLimit && (
            <button
              onClick={() => { setNewLimit(keyData.maxDevices); setIsEditingLimit(true); }}
              className="btn btn-secondary btn-xs"
              title="Изменить лимит"
            >
              <Settings size={11} /> Лимит: {keyData.maxDevices}
            </button>
          )
          : <span className="stat-chip">Лимит: {keyData.maxDevices}</span>
        )}
      </div>

      {isAdmin && isEditingLimit && (
        <div className="limit-editor">
          <span className="limit-label">Лимит (1–20):</span>
          <input
            type="number" min="1" max="20" value={newLimit}
            onChange={(e) => setNewLimit(parseInt(e.target.value) || 1)}
            className="input input-sm limit-input"
          />
          <button onClick={handleSaveLimit} className="btn btn-success btn-xs">
            <Check size={11} />
          </button>
          <button onClick={() => setIsEditingLimit(false)} className="btn btn-secondary btn-xs">
            <X size={11} />
          </button>
        </div>
      )}

      {isLoadingKeys ? (
        <div className="binding-loading">
          <div className="spinner" /> Загрузка...
        </div>
      ) : (
        <>
          {keyData?.keys?.length > 0 && (
            <div className="keys-summary-row">
              <div className="binding-status bound">
                <Key size={13} />
                {keyData.keyCount}/{keyData.maxDevices} ключей активно
              </div>
              <div className="keys-summary-actions">
                {!cryptoMode && (
                  <button
                    className={`btn btn-sm ${copiedAllKeys ? 'btn-success' : 'btn-secondary'}`}
                    onClick={handleCopyAllKeyLinks}
                    title="Скопировать ссылки всех устройств одним списком"
                  >
                    {copiedAllKeys
                      ? <><Check size={12} /> Скопировано ({keyData.keys.length})</>
                      : <><Copy size={12} /> Все ссылки ({keyData.keys.length})</>
                    }
                  </button>
                )}
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={handleCrypto4All}
                  disabled={crypto4AllRunning || keyData.keys.every(k => crypto4Results[k.key])}
                  title="Получить crypto4-ссылки сразу для всех ключей"
                >
                  {crypto4AllRunning
                    ? <><Loader size={12} className="spin-icon" /> Crypto4 {crypto4AllDone}/{keyData.keys.filter(k => !crypto4Results[k.key]).length + crypto4AllDone}</>
                    : <><Globe size={12} /> Crypto4 всем</>
                  }
                </button>
                {crypto4ReadyCount > 0 && (
                  <button
                    className={`btn btn-sm ${copiedAllCrypto4 ? 'btn-success' : 'btn-secondary'}`}
                    onClick={handleCopyAllCrypto4}
                    title="Скопировать все crypto4-ссылки одним списком"
                  >
                    {copiedAllCrypto4
                      ? <><Check size={12} /> Скопировано ({crypto4ReadyCount})</>
                      : <><Copy size={12} /> Все crypto4 ({crypto4ReadyCount})</>
                    }
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Сетка, а не столбик: в кабинете дилера панель занимает всю ширину окна,
              и в один ряд встаёт 2-4 карточки вместо одной длинной колонки. */}
          <div className="keys-grid">
          {keyData?.keys?.map((k) => (
            <div key={k.key} className="key-card">
              <div className="key-card-header">
                {editingKeyId === k.key ? (
                  <input
                    ref={labelInputRef}
                    className="key-label-input"
                    value={editingKeyLabel}
                    onChange={(e) => setEditingKeyLabel(e.target.value)}
                    onBlur={() => handleSaveLabel(k.key)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveLabel(k.key);
                      if (e.key === 'Escape') setEditingKeyId(null);
                    }}
                  />
                ) : (
                  <span className="key-card-title">{k.label}</span>
                )}
                <div className="key-card-actions">
                  {/* В режиме «только Happ» crypto4 делает главная кнопка карточки — глобус тут лишний */}
                  {!cryptoMode && (
                    <button
                      className="btn btn-ghost btn-xs btn-icon"
                      title="Crypto4 ссылка"
                      onClick={() => handleCrypto4(k.key)}
                      disabled={crypto4Loading === k.key}
                      style={{ color: crypto4Results[k.key] ? 'var(--success)' : undefined }}
                    >
                      {crypto4Loading === k.key
                        ? <Loader size={11} className="spin-icon" />
                        : <Globe size={11} />
                      }
                    </button>
                  )}
                  <button
                    className="btn btn-ghost btn-xs btn-icon"
                    title="Переименовать"
                    onClick={() => handleStartRelabel(k)}
                  >
                    <Pencil size={11} />
                  </button>
                  <button
                    className="btn btn-danger btn-xs"
                    title="Удалить ключ"
                    onClick={() => handleRevokeKey(k.key, k.label)}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
              <div className="key-card-meta">
                <span className="key-meta-item">
                  <Key size={10} />
                  <code>{k.key.substring(0, 8)}…</code>
                </span>
                <span className="key-meta-item">
                  Создан: {new Date(k.createdAt).toLocaleDateString('ru-RU')}
                </span>
                {k.lastUsed && (
                  <span className="key-meta-item">
                    Последний раз: {new Date(k.lastUsed).toLocaleString('ru-RU')}
                  </span>
                )}
                {k.lastIp && (
                  <span className="key-meta-item" style={{ color: 'var(--text-muted)' }}>
                    IP: {k.lastIp}
                  </span>
                )}
                {/* Привязок две: сильная (boundUserAgent — только Happ шлёт install-ID) и слабая
                    (boundWeakId — «приложение+платформа», всё, что известно про V2Box/Hiddify).
                    Раньше смотрели только на первую, поэтому ключ, честно работающий из V2Box,
                    показывался как «не подключено». */}
                <span className="key-meta-item" style={{ color: (k.boundUserAgent || k.boundWeakId) ? 'var(--success)' : 'var(--text-muted)' }}>
                  <Smartphone size={10} />
                  {k.boundUserAgent
                    ? (k.boundUserAgent.length > 35 ? k.boundUserAgent.substring(0, 35) + '…' : k.boundUserAgent)
                    : (k.boundWeakId || 'не подключено')}
                </span>
              </div>
              {k.blocked && (
                <div className="key-meta-item" style={{ color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <AlertTriangle size={11} />
                  Заблокирован — обнаружено использование с другого устройства
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {cryptoMode ? (
                  // Только Happ: обычную ссылку не раздаём, вместо неё кнопка crypto4
                  <button
                    className={`btn btn-sm key-copy-url ${crypto4Results[k.key] ? 'btn-success' : 'btn-secondary'}`}
                    onClick={() => crypto4Results[k.key] ? handleCopyCrypto4(k.key) : handleCrypto4(k.key)}
                    disabled={crypto4Loading === k.key}
                    style={{ flex: 1 }}
                  >
                    {crypto4Loading === k.key
                      ? <><Loader size={12} className="spin-icon" /> Получаю ссылку...</>
                      : crypto4Copied === k.key
                        ? <><Check size={12} /> Скопировано</>
                        : crypto4Results[k.key]
                          ? <><Copy size={12} /> Скопировать ссылку клиента</>
                          : <><Globe size={12} /> Получить ссылку клиента</>
                    }
                  </button>
                ) : (
                  <button
                    className={`btn btn-sm key-copy-url ${copiedKey === k.key ? 'btn-success' : 'btn-secondary'}`}
                    onClick={() => handleCopyKeyLink(k.key)}
                    style={{ flex: 1 }}
                  >
                    {copiedKey === k.key
                      ? <><Check size={12} /> Скопировано</>
                      : <><Copy size={12} /> Скопировать ссылку клиента</>
                    }
                  </button>
                )}
                {(k.boundUserAgent || k.blocked) && (
                  <button
                    className="btn btn-warning btn-sm"
                    title="Сбросить привязку — ключ снова станет свободным"
                    onClick={() => handleUnbindKey(k.key)}
                  >
                    <Unlink size={12} />
                  </button>
                )}
              </div>

              {crypto4Results[k.key] && (
                <div className="crypto4-result">
                  <div className="crypto4-label">
                    <Globe size={10} /> Crypto4 ссылка
                  </div>
                  <div className="crypto4-url-row">
                    <code className="crypto4-url">{crypto4Results[k.key]}</code>
                    <button
                      className={`btn btn-xs ${crypto4Copied === k.key ? 'btn-success' : 'btn-secondary'}`}
                      onClick={() => handleCopyCrypto4(k.key)}
                      title="Скопировать Crypto4 ссылку"
                    >
                      {crypto4Copied === k.key ? <Check size={11} /> : <Copy size={11} />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          </div>

          {keyData?.keys?.length === 0 && (
            <div className="binding-status unbound">
              <Key size={13} /> Нет ключей — создайте первый
            </div>
          )}

          <button
            onClick={handleGenerateKey}
            disabled={generatingKey || !canGenerateKey}
            className="btn btn-primary btn-sm"
            style={{ marginTop: 10, width: '100%' }}
            title={!canGenerateKey ? `Достигнут лимит (${keyData?.maxDevices})` : ''}
          >
            {generatingKey
              ? <><div className="spinner" style={{ width: 13, height: 13, borderWidth: 2, margin: 0 }} /> Создание...</>
              : <><Plus size={13} /> Создать новый ключ</>
            }
          </button>

          {keyData?.keys?.length > 0 && (
            <button
              onClick={handleUnbindAll}
              disabled={unbindingAll}
              className="btn btn-warning btn-sm"
              style={{ marginTop: 6, width: '100%' }}
              title="Снять привязку у всех ключей — ссылки останутся рабочими"
            >
              {unbindingAll
                ? <><div className="spinner" style={{ width: 13, height: 13, borderWidth: 2, margin: 0 }} /> Сброс...</>
                : <><Unlink size={13} /> Сбросить все привязки</>
              }
            </button>
          )}

          {isAdmin && keyData?.keys?.length > 0 && (
            <button
              onClick={handleRevokeAll}
              className="btn btn-danger btn-sm"
              style={{ marginTop: 6, width: '100%' }}
            >
              <RefreshCw size={13} /> Отозвать все ключи
            </button>
          )}
        </>
      )}

      <div className="info-notice" style={{ marginTop: 10 }}>
        <Key size={12} style={{ flexShrink: 0, marginTop: 1 }} />
        Каждое устройство получает свой UUID-ключ в URL. При первом подключении ключ привязывается к VPN-приложению.
      </div>
    </div>
  );
}

export default AccessKeysPanel;
